import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Building2,
  Users,
  CreditCard,
  Activity,
  Settings,
  MoreVertical,
  Eye,
  KeyRound,
  Shield,
  UserX,
  LogOut,
  Trash2,
  Mail,
  Phone,
  Calendar,
  Clock,
  FileText,
  Send,
  AlertTriangle,
  CheckCircle2,
  Landmark,
  Receipt,
  Plus,
  Zap,
  ShieldAlert
} from 'lucide-react';
import { PlanEmblem, SubStatusBadge } from './OwnerAdminCustomers';
import WorkspaceStripeTruth from './WorkspaceStripeTruth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Platform-fee mode summary helper (0.6% capped $25 default, "off" = waived).
function summarizePlatformFee(ps) {
  const mode = ps?.platform_fee_mode;
  if (!mode) return { label: 'Platform default (0.6% capped at $25)', waived: false, active: true };
  if (mode === 'off') return { label: 'Waived — no platform fee on this workspace', waived: true, active: false };
  if (mode === 'percent') {
    const pct = ps.platform_fee_percent ?? 0.6;
    const cap = (ps.platform_fee_cap_cents ?? 2500) / 100;
    return { label: `${pct}% (capped at $${cap.toFixed(2)})`, waived: false, active: true };
  }
  if (mode === 'flat') {
    const flat = (ps.platform_fee_flat_cents ?? 0) / 100;
    return { label: `$${flat.toFixed(2)} flat per payment`, waived: false, active: true };
  }
  return { label: 'Platform default', waived: false, active: true };
}

// Human-readable labels + icons for admin_audit_log action codes. Actions
// not in this map fall back to a de-underscored display.
const ACTION_LABELS = {
  admin_view_start: 'Opened Admin View',
  admin_view_end: 'Exited Admin View',
  impersonate_start: 'Started Impersonation',
  reset_password: 'Sent password reset email',
  send_magic_link: 'Sent magic sign-in link',
  view_customer: 'Viewed customer profile',
  add_customer_note: 'Added internal note',
};
const ACTION_ICONS = {
  admin_view_start: ShieldAlert,
  admin_view_end: ShieldAlert,
  impersonate_start: Eye,
  reset_password: KeyRound,
  send_magic_link: Zap,
  view_customer: Activity,
  add_customer_note: FileText,
};

export default function OwnerAdminCustomerDetail() {
  const { workspaceId } = useParams();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [adminActivity, setAdminActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [duplicates, setDuplicates] = useState(null);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [mergeDialog, setMergeDialog] = useState({ open: false, sibling: null });
  const [mergeSubmitting, setMergeSubmitting] = useState(false);
  
  // Impersonation dialog state
  const [impersonateDialog, setImpersonateDialog] = useState({ open: false, user: null });
  const [impersonateReason, setImpersonateReason] = useState('');
  const [impersonating, setImpersonating] = useState(false);

  // Platform-fee waiver
  const [waiveDialog, setWaiveDialog] = useState(false);
  const [waiveReason, setWaiveReason] = useState('');
  const [waiveSubmitting, setWaiveSubmitting] = useState(false);
  const [feeSubmitting, setFeeSubmitting] = useState(false);

  // Delete customer
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  const fetchCustomer = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/customers/${workspaceId}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCustomer(data);
      } else {
        toast.error('Failed to load customer');
      }
    } catch (error) {
      console.error('Failed to fetch customer:', error);
      toast.error('Failed to load customer');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchCustomer();
  }, [fetchCustomer]);

  // Load recent admin activity when the tab opens (lazy — many workspaces
  // will never have this viewed, so we skip the extra round-trip until
  // needed). Refetches on tab click for freshness.
  const fetchAdminActivity = useCallback(async () => {
    setActivityLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/owner-admin/customers/${workspaceId}/admin-activity?limit=100`,
        { credentials: 'include' },
      );
      if (response.ok) {
        const data = await response.json();
        setAdminActivity(data.activity || []);
      }
    } catch (e) { /* fail silent, activity is non-critical */ }
    finally {
      setActivityLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (activeTab === 'activity') fetchAdminActivity();
  }, [activeTab, fetchAdminActivity]);

  const fetchDuplicates = useCallback(async () => {
    setDuplicatesLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/owner-admin/workspaces/${workspaceId}/duplicates`,
        { credentials: 'include' },
      );
      if (response.ok) {
        setDuplicates(await response.json());
      }
    } catch (e) { /* silent */ }
    finally {
      setDuplicatesLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (activeTab === 'duplicates') fetchDuplicates();
  }, [activeTab, fetchDuplicates]);

  const handleMerge = async () => {
    if (!mergeDialog.sibling) return;
    setMergeSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/workspaces/merge`, {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keep_workspace_id: workspaceId,
          archive_workspace_id: mergeDialog.sibling.workspace_id,
          migrate_members: true,
          reason: 'admin merge from Customer Detail',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(data.detail || 'Merge failed');
        return;
      }
      const parts = [`${data.stats?.members_migrated || 0} member(s) migrated`];
      if (data.stats?.members_deduped) parts.push(`${data.stats.members_deduped} deduped`);
      toast.success(`Merged. ${parts.join(', ')}.`);
      if ((data.warnings || []).length > 0) {
        // Surface each warning as its own persistent toast — usually about
        // an active Stripe sub the admin needs to cancel manually.
        data.warnings.forEach((w) => toast.warning(w, { duration: 15000 }));
      }
      setMergeDialog({ open: false, sibling: null });
      fetchDuplicates();
    } catch (e) {
      toast.error('Merge failed');
    } finally {
      setMergeSubmitting(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    setAddingNote(true);
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/customers/${workspaceId}/notes?content=${encodeURIComponent(newNote)}`, {
        credentials: 'include',
        method: 'POST'
      });
      
      if (response.ok) {
        toast.success('Note added');
        setNewNote('');
        fetchCustomer();
      } else {
        toast.error('Failed to add note');
      }
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleImpersonate = async () => {
    if (!impersonateReason.trim()) {
      toast.error('Please provide a reason for impersonation');
      return;
    }
    
    setImpersonating(true);
    // Open synchronously within the click gesture so pop-up blockers allow it.
    const win = window.open('about:blank', '_blank');
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/users/${impersonateDialog.user.user_id}/impersonate`, {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: impersonateDialog.user.user_id, reason: impersonateReason })
      });
      
      if (response.ok) {
        const data = await response.json();
        const url = `/impersonate?token=${encodeURIComponent(data.token)}`;
        if (win && !win.closed) {
          win.location.replace(url);
          toast.success('Impersonation session started');
        } else {
          toast.success('Impersonation session ready', {
            duration: 15000,
            action: { label: 'Open client session', onClick: () => window.open(url, '_blank') },
          });
        }
        setImpersonateDialog({ open: false, user: null });
        setImpersonateReason('');
      } else {
        if (win) win.close();
        const error = await response.json();
        toast.error(error.detail || 'Failed to start impersonation');
      }
    } catch (error) {
      if (win) win.close();
      toast.error('Failed to start impersonation');
    } finally {
      setImpersonating(false);
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/users/${userId}/reset-password`, {
        credentials: 'include',
        method: 'POST'
      });
      
      if (response.ok) {
        toast.success('Password reset email sent');
      } else {
        toast.error('Failed to send password reset');
      }
    } catch (error) {
      toast.error('Failed to send password reset');
    }
  };

  const handleSendMagicLink = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/users/${userId}/magic-link`, {
        credentials: 'include',
        method: 'POST'
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast.success(data.message || 'Magic sign-in link sent');
      } else {
        toast.error(data.detail || 'Failed to send magic link');
      }
    } catch (error) {
      toast.error('Failed to send magic link');
    }
  };

  const handleWaiveFee = async () => {
    if (!waiveReason.trim() || waiveReason.trim().length < 3) {
      toast.error('Please enter a reason (min 3 chars)');
      return;
    }
    setWaiveSubmitting(true);
    try {
      const r = await fetch(
        `${API_URL}/api/owner-admin/platform-fee/waive/${workspaceId}`,
        {
          credentials: 'include',
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: waiveReason.trim() }),
        },
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(data.detail || 'Waive failed');
        return;
      }
      toast.success('Platform fee waived for this workspace');
      setWaiveDialog(false);
      setWaiveReason('');
      fetchCustomer();
    } catch (e) {
      toast.error('Waive failed');
    } finally {
      setWaiveSubmitting(false);
    }
  };

  const handleEnableFee = async () => {
    setFeeSubmitting(true);
    try {
      const r = await fetch(
        `${API_URL}/api/owner-admin/platform-fee/enable/${workspaceId}`,
        { credentials: 'include', method: 'POST' },
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(data.detail || 'Enable failed');
        return;
      }
      toast.success('Platform fee re-enabled at default (0.6% / $25 cap)');
      fetchCustomer();
    } catch (e) {
      toast.error('Enable failed');
    } finally {
      setFeeSubmitting(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (deleteConfirm !== customer?.name) {
      toast.error('Type the workspace name exactly to confirm');
      return;
    }
    setDeleting(true);
    try {
      const r = await fetch(
        `${API_URL}/api/owner-admin/workspaces/${workspaceId}/archive`,
        { credentials: 'include', method: 'POST' },
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(data.detail || 'Delete failed');
        return;
      }
      toast.success('Workspace archived — hidden from customers');
      setDeleteDialog(false);
      setDeleteConfirm('');
      // Route back to customer list
      window.location.href = '/owner-admin/customers';
    } catch (e) {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleStartAdminView = async () => {
    // Synchronously open the tab so the popup blocker allows it, then fill
    // the location once the token is minted.
    const win = window.open('about:blank', '_blank');
    try {
      const response = await fetch(
        `${API_URL}/api/owner-admin/workspaces/${workspaceId}/admin-view/start`,
        {
          credentials: 'include',
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: 'admin support work' }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.token) {
        if (win) win.close();
        toast.error(data.detail || 'Failed to start Admin View');
        return;
      }
      const url = `/admin-view?token=${encodeURIComponent(data.token)}&ws=${encodeURIComponent(workspaceId)}`;
      if (win) win.location.href = url;
      else window.open(url, '_blank');
      toast.success('Admin View opened in a new tab');
    } catch (e) {
      if (win) win.close();
      toast.error('Failed to start Admin View');
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64 bg-white/10" />
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3].map(i => (
            <Card key={i} className="bg-card border-white/15">
              <CardContent className="pt-6">
                <Skeleton className="h-4 w-24 bg-white/10" />
                <Skeleton className="h-8 w-32 mt-2 bg-white/10" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 mx-auto text-yellow-500 mb-3" />
        <p className="text-white text-lg">Customer not found</p>
        <Button asChild className="mt-4">
          <Link to="/owner-admin/customers">Back to Customers</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild className="text-muted-foreground">
          <Link to="/owner-admin/customers">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-emerald-500/10">
              <Building2 className="h-6 w-6 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{customer.name}</h1>
              <p className="text-sm text-muted-foreground">{customer.owner_email || customer.workspace_id}</p>
            </div>
            {customer.billing && (
              <PlanEmblem planId={customer.billing.plan_id} cadence={customer.billing.billing_cadence} />
            )}
            <Badge className="ml-2 bg-emerald-500/20 text-emerald-400">
              {customer.status || 'Active'}
            </Badge>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="border-white/20 text-muted-foreground/80">
              Actions
              <MoreVertical className="h-4 w-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={handleStartAdminView}
              data-testid="customer-view-as-admin"
              className="text-red-400 focus:text-red-300 focus:bg-red-500/10"
            >
              <ShieldAlert className="h-4 w-4 mr-2" />
              View as Admin (write access)
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <Mail className="h-4 w-4 mr-2" />
              Send Email
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-yellow-400">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Suspend Account
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-400 focus:text-red-300 focus:bg-red-500/10"
              onClick={() => setDeleteDialog(true)}
              data-testid="customer-delete-account"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Account
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Users</p>
                <p className="text-2xl font-bold text-white">{customer.users?.length || 0}</p>
              </div>
              <Users className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Accounts</p>
                <p className="text-2xl font-bold text-white">{customer.stats?.account_count || 0}</p>
              </div>
              <FileText className="h-8 w-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Transactions</p>
                <p className="text-2xl font-bold text-white">{customer.stats?.transaction_count || 0}</p>
              </div>
              <Receipt className="h-8 w-8 text-violet-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Bank Accounts</p>
                <p className="text-2xl font-bold text-white">{customer.stats?.bank_accounts || 0}</p>
              </div>
              <Landmark className="h-8 w-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-card border border-white/15">
          <TabsTrigger value="overview" className="data-[state=active]:bg-white/10">Overview</TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-white/10">Users</TabsTrigger>
          <TabsTrigger value="activity" className="data-[state=active]:bg-white/10">Activity</TabsTrigger>
          <TabsTrigger value="duplicates" className="data-[state=active]:bg-white/10" data-testid="tab-duplicates">Duplicates</TabsTrigger>
          <TabsTrigger value="notes" className="data-[state=active]:bg-white/10">Internal Notes</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {customer.billing && (
              <Card className="bg-card border-white/15 md:col-span-2" data-testid="plan-billing-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-white flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-emerald-400" />
                    Plan &amp; Billing
                  </CardTitle>
                  <div className="flex items-center gap-3">
                    <PlanEmblem planId={customer.billing.plan_id} cadence={customer.billing.billing_cadence} />
                    <SubStatusBadge customer={customer.billing} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">MRR</p>
                      <p className="text-xl font-semibold text-white mt-1" data-testid="detail-mrr">
                        {customer.billing.mrr > 0 ? (
                          <span className="text-emerald-400">${customer.billing.mrr.toFixed(2)}<span className="text-sm text-muted-foreground">/mo</span></span>
                        ) : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Plan Price</p>
                      <p className="text-xl font-semibold text-white mt-1">
                        ${(customer.billing.plan_price || 0).toFixed(2)}
                        <span className="text-sm text-muted-foreground">/{customer.billing.billing_cadence === 'annual' ? 'mo · billed annually' : 'mo'}</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">AI Credits</p>
                      <p className="text-xl font-semibold text-white mt-1">
                        {customer.billing.credits_used_this_period || 0}
                        <span className="text-sm text-muted-foreground"> / {customer.billing.monthly_credits || 0} used</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Billing Owner</p>
                      <p className="text-sm text-white mt-2">{customer.owner_name || '—'}</p>
                      <p className="text-xs text-muted-foreground">{customer.owner_email || '—'}</p>
                    </div>
                  </div>
                  {(customer.billing.stripe_customer_id || customer.billing.stripe_subscription_id) && (
                    <div className="mt-4 pt-4 border-t border-white/10 grid gap-2 sm:grid-cols-2 text-sm">
                      <div className="flex justify-between gap-3">
                        <span className="text-muted-foreground">Stripe Customer</span>
                        <span className="text-white font-mono text-xs truncate" data-testid="stripe-customer-id">{customer.billing.stripe_customer_id || '—'}</span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-muted-foreground">Stripe Subscription</span>
                        <span className="text-white font-mono text-xs truncate">{customer.billing.stripe_subscription_id || '—'}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            <Card className="bg-card border-white/15">
              <CardHeader>
                <CardTitle className="text-white">Account Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Workspace ID</span>
                  <span className="text-white font-mono text-sm">{customer.workspace_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Company Name</span>
                  <span className="text-white">{customer.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge className="bg-emerald-500/20 text-emerald-400">{customer.status || 'Active'}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created</span>
                  <span className="text-white">{formatDate(customer.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Fiscal Year End</span>
                  <span className="text-white">{customer.fiscal_year_end || 'December'}</span>
                </div>
              </CardContent>
            </Card>

            {/* Platform Fee Override (Owner Admin only). Waiving = zero
                application_fee on this workspace's Stripe Connect payments. */}
            {(() => {
              const feeSummary = summarizePlatformFee(customer.payment_settings);
              return (
                <Card className="bg-card border-white/15" data-testid="platform-fee-card">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-emerald-400" />
                      Platform Fee Override
                    </CardTitle>
                    <CardDescription className="text-muted-foreground">
                      CountBucks application_fee applied to this workspace&apos;s Stripe Connect invoice payments.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        {feeSummary.waived ? (
                          <Badge className="bg-red-500/10 text-red-300 border-red-500/30">Waived</Badge>
                        ) : (
                          <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30">Active</Badge>
                        )}
                        <span className="text-sm text-white" data-testid="platform-fee-summary">{feeSummary.label}</span>
                      </div>
                    </div>

                    {feeSummary.waived && customer.platform_fee_waiver_reason && (
                      <div className="rounded-md bg-white/5 border border-white/10 p-3 text-xs text-muted-foreground">
                        <div className="text-white/80 font-medium mb-1">Waiver reason</div>
                        <div>&ldquo;{customer.platform_fee_waiver_reason}&rdquo;</div>
                        <div className="mt-1 text-[10px]">
                          by <strong className="text-white/70">{customer.platform_fee_waived_by || '—'}</strong>
                          {customer.platform_fee_waived_at && (
                            <> · {formatDateTime(customer.platform_fee_waived_at)}</>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 pt-2">
                      {feeSummary.waived ? (
                        <Button
                          onClick={handleEnableFee}
                          disabled={feeSubmitting}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="platform-fee-enable-btn"
                        >
                          {feeSubmitting ? 'Enabling…' : 'Re-enable at default (0.6% / $25 cap)'}
                        </Button>
                      ) : (
                        <Button
                          onClick={() => setWaiveDialog(true)}
                          variant="outline"
                          className="border-red-400/40 text-red-300 hover:bg-red-500/10"
                          data-testid="platform-fee-waive-btn"
                        >
                          <ShieldAlert className="h-4 w-4 mr-2" />
                          Waive platform fee
                        </Button>
                      )}
                      <Button
                        asChild
                        variant="ghost"
                        className="text-emerald-400 hover:text-emerald-300"
                      >
                        <Link
                          to={`/owner-admin/payments?workspace_id=${encodeURIComponent(workspaceId)}`}
                          data-testid="platform-fee-view-payments"
                        >
                          View payments ledger →
                        </Link>
                      </Button>
                    </div>
                    <p className="text-[11px] text-muted-foreground/70">
                      Owner-only. Waiving zeroes CountBucks&apos; platform revenue for this workspace and is audit-logged.
                    </p>
                  </CardContent>
                </Card>
              );
            })()}

            <WorkspaceStripeTruth workspaceId={workspaceId} />

            <Card className="bg-card border-white/15">
              <CardHeader>
                <CardTitle className="text-white">Usage Statistics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Chart of Accounts</span>
                  <span className="text-white">{customer.stats?.account_count || 0} accounts</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Journal Entries</span>
                  <span className="text-white">{customer.stats?.transaction_count || 0} entries</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Bank Connections</span>
                  <span className="text-white">{customer.stats?.bank_accounts || 0} accounts</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Team Members</span>
                  <span className="text-white">{customer.users?.length || 0} users</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-6">
          <Card className="bg-card border-white/15">
            <CardHeader>
              <CardTitle className="text-white">Team Members</CardTitle>
              <CardDescription className="text-muted-foreground">
                Users with access to this workspace
              </CardDescription>
            </CardHeader>
            <CardContent>
              {customer.users?.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No users found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/15">
                      <TableHead className="text-muted-foreground">User</TableHead>
                      <TableHead className="text-muted-foreground">Role</TableHead>
                      <TableHead className="text-muted-foreground">Last Login</TableHead>
                      <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customer.users?.map((user) => (
                      <TableRow key={user.user_id} className="border-white/15 hover:bg-white/[0.07]">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                              <span className="text-sm font-medium text-white">
                                {user.name?.charAt(0) || user.email?.charAt(0) || '?'}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-white">{user.name || 'Unnamed'}</p>
                              <p className="text-xs text-muted-foreground">{user.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="border-white/20 text-muted-foreground/80 capitalize">
                            {(user.role || 'member').replace(/_/g, ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDateTime(user.last_login)}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-muted-foreground">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setImpersonateDialog({ open: true, user })}>
                                <Eye className="h-4 w-4 mr-2" />
                                Impersonate
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleResetPassword(user.user_id)} data-testid={`customer-user-reset-password-${user.user_id}`}>
                                <KeyRound className="h-4 w-4 mr-2" />
                                Reset Password
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleSendMagicLink(user.user_id)} data-testid={`customer-user-magic-link-${user.user_id}`}>
                                <Zap className="h-4 w-4 mr-2" />
                                Send Magic Link
                              </DropdownMenuItem>
                              <DropdownMenuItem>
                                <Shield className="h-4 w-4 mr-2" />
                                Reset 2FA
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem>
                                <LogOut className="h-4 w-4 mr-2" />
                                Force Logout
                              </DropdownMenuItem>
                              <DropdownMenuItem className="text-red-400">
                                <UserX className="h-4 w-4 mr-2" />
                                Disable Account
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="mt-6">
          <Card className="bg-card border-white/15">
            <CardHeader>
              <CardTitle className="text-white">Recent Admin Activity</CardTitle>
              <CardDescription className="text-muted-foreground">
                Everything CountBucks support staff has done inside this workspace — impersonations, admin-view sessions, password resets, and audit-logged changes. Invisible to the customer.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {activityLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-14 w-full bg-white/5" />
                  ))}
                </div>
              ) : adminActivity.length === 0 ? (
                <div className="text-center py-8" data-testid="admin-activity-empty">
                  <Activity className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No admin activity recorded yet on this workspace.</p>
                  <p className="text-xs text-muted-foreground/70 mt-1">Actions taken from Owner Admin (impersonate, view-as-admin, reset password, magic link) will appear here.</p>
                </div>
              ) : (
                <div className="space-y-2" data-testid="admin-activity-list">
                  {adminActivity.map((row) => {
                    const label = ACTION_LABELS[row.action] || row.action.replace(/_/g, ' ');
                    const icon = ACTION_ICONS[row.action] || Activity;
                    const Icon = icon;
                    const when = row.timestamp ? new Date(row.timestamp).toLocaleString() : '';
                    const reason = row.details?.reason;
                    const targetEmail = row.details?.user_email;
                    return (
                      <div
                        key={row.log_id}
                        data-testid={`admin-activity-row-${row.log_id}`}
                        className="flex items-start gap-3 p-3 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-colors"
                      >
                        <div className="p-2 rounded-md bg-emerald-500/10 text-emerald-400 shrink-0">
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-white font-medium">{label}</span>
                            {targetEmail && (
                              <span className="text-xs text-muted-foreground truncate">
                                → {targetEmail}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                            <span>by <strong className="text-muted-foreground/90">{row.admin_email}</strong></span>
                            <span>·</span>
                            <span>{when}</span>
                            {reason && (
                              <>
                                <span>·</span>
                                <span className="italic truncate">&ldquo;{reason}&rdquo;</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Duplicates & Merge Tab */}
        <TabsContent value="duplicates" className="mt-6" data-testid="duplicates-tab-content">
          <Card className="bg-card border-white/15">
            <CardHeader>
              <CardTitle className="text-white">Sibling Workspaces (same owner email)</CardTitle>
              <CardDescription className="text-muted-foreground">
                Other workspaces owned by <strong className="text-muted-foreground/90">{duplicates?.anchor_owner_email || '—'}</strong>.
                Compare side-by-side, then merge to consolidate members into this workspace and archive the loser.
                Billing/Stripe is <strong>NOT</strong> migrated automatically — cancel the other sub manually after merging.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {duplicatesLoading ? (
                <Skeleton className="h-24 w-full bg-white/5" />
              ) : !duplicates || (duplicates.siblings || []).length === 0 ? (
                <div className="text-center py-8" data-testid="duplicates-empty">
                  <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No sibling workspaces — this owner only has one.</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="duplicates-list">
                  {duplicates.siblings.map((sib) => (
                    <div
                      key={sib.workspace_id}
                      data-testid={`duplicate-row-${sib.workspace_id}`}
                      className="p-4 rounded-lg bg-white/5 border border-white/10"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-emerald-400 shrink-0" />
                            <p className="text-white font-medium truncate">{sib.name || sib.workspace_id}</p>
                            {sib.name_match ? (
                              <Badge variant="outline" className="text-[10px] border-red-400/30 text-red-400 bg-red-500/10" title="Same business name AND same owner email — a true duplicate signup.">
                                Duplicate name
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] border-white/20 text-slate-400 bg-white/5" title="Same owner email but a different business name — probably a separate business.">
                                Different business
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-[10px]">{sib.plan_label}</Badge>
                            {sib.subscription_status === 'active' && (
                              <Badge variant="outline" className="text-[10px] border-emerald-400/30 text-emerald-400 bg-emerald-500/10">Active sub</Badge>
                            )}
                            {sib.subscription_status === 'trialing' && (
                              <Badge variant="outline" className="text-[10px] border-blue-400/30 text-blue-400 bg-blue-500/10">Trialing</Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-4 gap-4 mt-3 text-xs">
                            <div>
                              <p className="text-muted-foreground">MRR</p>
                              <p className="text-white font-medium">${(sib.mrr || 0).toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Members</p>
                              <p className="text-white font-medium">{sib.member_count}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Documents</p>
                              <p className="text-white font-medium">{sib.doc_count}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Transactions</p>
                              <p className="text-white font-medium">{sib.txn_count}</p>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground/70 mt-2">
                            Created {formatDate(sib.created_at)} · ID <code className="text-muted-foreground/90">{sib.workspace_id}</code>
                          </p>
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                          <Button
                            asChild
                            variant="outline"
                            size="sm"
                            className="border-white/20"
                            data-testid={`duplicate-open-${sib.workspace_id}`}
                          >
                            <Link to={`/owner-admin/customers/${sib.workspace_id}`}>Open</Link>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setMergeDialog({ open: true, sibling: sib })}
                            data-testid={`duplicate-merge-${sib.workspace_id}`}
                            className="border-red-400/40 text-red-400 hover:bg-red-500/10"
                          >
                            Merge into this
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="mt-6">
          <Card className="bg-card border-white/15">
            <CardHeader>
              <CardTitle className="text-white">Internal Notes</CardTitle>
              <CardDescription className="text-muted-foreground">
                Private notes visible only to admin staff
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add Note */}
              <div className="flex gap-3">
                <Textarea
                  placeholder="Add a note about this customer..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  className="bg-white/10 border-white/20 text-white resize-none"
                  rows={3}
                />
                <Button 
                  onClick={handleAddNote} 
                  disabled={addingNote || !newNote.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>

              {/* Notes List */}
              {customer.internal_notes?.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No notes yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {customer.internal_notes?.map((note) => (
                    <div key={note.note_id} className="p-4 rounded-lg bg-white/[0.07] border border-white/15">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-emerald-400">{note.admin_name}</span>
                        <span className="text-xs text-muted-foreground">{formatDateTime(note.created_at)}</span>
                      </div>
                      <p className="text-muted-foreground/80">{note.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Impersonation Dialog */}
      <Dialog open={impersonateDialog.open} onOpenChange={(open) => !open && setImpersonateDialog({ open: false, user: null })}>
        <DialogContent className="bg-card border-white/15">
          <DialogHeader>
            <DialogTitle className="text-white">Impersonate User</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              You are about to view the app as <strong className="text-white">{impersonateDialog.user?.name || impersonateDialog.user?.email}</strong>.
              This action will be logged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-500 shrink-0" />
                <div className="text-sm text-yellow-200">
                  <p className="font-medium">Important</p>
                  <p>All actions during impersonation are logged and attributed to your admin account.</p>
                </div>
              </div>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Reason for impersonation *</label>
              <Textarea
                placeholder="e.g., Troubleshooting user-reported issue #1234"
                value={impersonateReason}
                onChange={(e) => setImpersonateReason(e.target.value)}
                className="bg-white/10 border-white/20 text-white"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setImpersonateDialog({ open: false, user: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleImpersonate}
              disabled={impersonating || !impersonateReason.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {impersonating ? 'Starting...' : 'Start Impersonation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Confirmation Dialog */}
      <Dialog
        open={mergeDialog.open}
        onOpenChange={(open) => !open && setMergeDialog({ open: false, sibling: null })}
      >
        <DialogContent className="bg-card border-white/15" data-testid="merge-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Merge Workspaces</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Migrate members from <strong className="text-white">{mergeDialog.sibling?.name}</strong> into this workspace,
              then archive the source. Data (documents, transactions) stays on the archived workspace and is unreachable to the customer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-500 shrink-0" />
                <div className="text-sm text-yellow-200">
                  <p className="font-medium">Billing is NOT migrated</p>
                  <p>If the source workspace&apos;s owner has an active Stripe subscription, cancel it manually to prevent double-charging. This tool will surface a warning.</p>
                </div>
              </div>
            </div>
            {mergeDialog.sibling && (
              <div className="text-sm text-muted-foreground space-y-1">
                <p>· {mergeDialog.sibling.member_count} member(s) will be re-keyed onto this workspace (dedupe on user_id).</p>
                <p>· {mergeDialog.sibling.doc_count} document(s) and {mergeDialog.sibling.txn_count} transaction(s) stay on the archived workspace.</p>
                <p>· Source will be marked <code className="text-muted-foreground/90">archived=true, merged_into={workspaceId}</code>.</p>
                <p>· Audit-logged as <code className="text-muted-foreground/90">workspace_merge</code>.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMergeDialog({ open: false, sibling: null })}>
              Cancel
            </Button>
            <Button
              onClick={handleMerge}
              disabled={mergeSubmitting}
              data-testid="merge-confirm-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {mergeSubmitting ? 'Merging…' : 'Merge & Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Waive Platform Fee Dialog */}
      <Dialog open={waiveDialog} onOpenChange={(o) => { if (!o) { setWaiveDialog(false); setWaiveReason(''); } }}>
        <DialogContent className="bg-card border-white/15" data-testid="waive-fee-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Waive Platform Fee</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              CountBucks will collect <strong className="text-white">no platform fee</strong> on this workspace&apos;s Stripe Connect invoice payments until re-enabled. Audit-logged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-500 shrink-0" />
                <div className="text-sm text-yellow-200">
                  <p className="font-medium">Reduces platform revenue</p>
                  <p>Use for VIP/enterprise deals or comped accounts. Cannot be undone silently — the customer never sees this.</p>
                </div>
              </div>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Reason (required, audit-logged) *</label>
              <Textarea
                placeholder="e.g., VIP customer — comped for 6 months per Q1-2026 partnership deal"
                value={waiveReason}
                onChange={(e) => setWaiveReason(e.target.value)}
                className="bg-white/10 border-white/20 text-white"
                rows={3}
                data-testid="waive-fee-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setWaiveDialog(false); setWaiveReason(''); }}>
              Cancel
            </Button>
            <Button
              onClick={handleWaiveFee}
              disabled={waiveSubmitting || waiveReason.trim().length < 3}
              className="bg-red-600 hover:bg-red-700"
              data-testid="waive-fee-confirm-btn"
            >
              {waiveSubmitting ? 'Waiving…' : 'Waive platform fee'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete/Archive Customer Dialog */}
      <Dialog open={deleteDialog} onOpenChange={(o) => { if (!o) { setDeleteDialog(false); setDeleteConfirm(''); } }}>
        <DialogContent className="bg-card border-white/15" data-testid="delete-customer-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Delete Customer</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              This will <strong className="text-white">archive</strong> {customer?.name}. The workspace will be hidden from all customer-facing views. Data is preserved (recoverable), Stripe subscriptions are <strong>NOT</strong> cancelled automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 text-red-400 shrink-0" />
                <div className="text-sm text-red-200 space-y-1">
                  <p className="font-medium">Cancel their Stripe sub first</p>
                  <p>If this customer still has an active subscription, cancel it in Stripe before archiving to avoid continued billing.</p>
                </div>
              </div>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">
                To confirm, type the workspace name: <strong className="text-white">{customer?.name}</strong>
              </label>
              <Input
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder={customer?.name || ''}
                className="bg-white/10 border-white/20 text-white"
                data-testid="delete-customer-confirm-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setDeleteDialog(false); setDeleteConfirm(''); }}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteCustomer}
              disabled={deleting || deleteConfirm !== customer?.name}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-customer-confirm-btn"
            >
              {deleting ? 'Archiving…' : 'Archive workspace'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
